/**
 * House of Dusk · module UNIQUE d'ecriture panier (lot 1). Contrat complet :
 * 00_AI_BRAIN/06_RELEVE_THEME/lots/lot1_fondations.md.
 * Chaque ecriture : CartLinesUpdateEvent (@shopify/events) emis avant la requete avec sa
 * promesse ; rendu des sections cart-items-component[data-section-id] ; promesse resolue avec
 * { cart, detail: { sections, items, itemCount, source, didError } }. 429 : une relance puis
 * « Try again », pour les ecritures comme pour la relecture de /cart.js. Une ecriture reussie
 * ne remonte jamais d'erreur (pas de second ajout par un nouveau clic). Ecritures en file ;
 * verrou tenu en JavaScript (WeakSet par bouton, une seule conversion en set a la fois) : un
 * morph du tiroir ne le leve pas. Apres un retrait ou une conversion, le focus va a une cible
 * stable et le nouvel etat du panier est annonce ([data-sh-cart-live]).
 * Declencheurs <button type="button"> : [data-sh-cart-add], [data-sh-cart-change],
 * [data-sh-cart-convert]. Set derriere l'adaptateur D14 (sh_set_engine : none par defaut).
 * API : window.ShCart = { add, change, remove, update, convertToSet, addSet, getCart, addRule,
 * engines }.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const RETRY_DELAY_MS = 1200;
const TRY_AGAIN = 'Try again';

// Editeur de theme (Dimi, 15/09/2026) : la vitrine y tourne dans une iframe de l'admin Shopify, ou
// le navigateur refuse le cookie `cart` (SameSite=Lax). L'ajout repond 200 mais chaque requete repart
// d'un panier neuf : /cart.js revient vide et le tiroir s'ouvrait sur « Your cart is empty ». Limite
// de Shopify, reproduite sur la boutique en retirant le cookie. Dans l'editeur seulement, le tiroir
// reste ferme et le bouton l'explique ; la boutique ne passe jamais ici (Shopify.designMode absent).
const EDITOR_NOTE =
  "Aperçu de l'éditeur : Shopify n'y garde pas le panier. Teste l'ajout sur la boutique, dans un onglet normal.";
const inEditor = () => Boolean(window.Shopify && window.Shopify.designMode);

// `Theme` : constante globale de snippets/scripts.liquid (routes du panier).
// eslint-disable-next-line no-undef
const routes = () => (typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {});
const endpoint = {
  add: () => routes().cart_add_url || '/cart/add.js',
  change: () => `${routes().cart_change_url || '/cart/change'}.js`,
  update: () => `${routes().cart_update_url || '/cart/update'}.js`,
  cart: () => `${routes().cart_url || '/cart'}.js`,
};
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export class ShCartError extends Error {
  constructor(message, code = 'INVALID', status = 0) {
    super(message);
    this.name = 'ShCartError';
    this.code = code; // 'INVALID' ou 'SERVICE_UNAVAILABLE' (codes de CartErrorEvent)
    this.status = status;
  }
}

// Sections panier presentes dans la page (tiroir, /cart), rendues par la reponse.
function cartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

function retryDelay(response) {
  const seconds = Number(response.headers.get('Retry-After'));
  return Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds * 1000, 5000) : RETRY_DELAY_MS;
}

// POST JSON ; 429 : une relance, puis « Try again ». 422 : message de Shopify (stock...).
async function post(url, body, tryAgain) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
    } catch {
      throw new ShCartError(tryAgain, 'SERVICE_UNAVAILABLE');
    }
    if (response.status === 429) {
      if (attempt === 0) {
        await wait(retryDelay(response));
        continue;
      }
      throw new ShCartError(tryAgain, 'SERVICE_UNAVAILABLE', 429);
    }
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    if (!response.ok || (data && typeof data.status === 'number' && data.status >= 400)) {
      throw new ShCartError(String((data && (data.description || data.message)) || tryAgain), 'INVALID', response.status);
    }
    return data || {};
  }
  throw new ShCartError(tryAgain, 'SERVICE_UNAVAILABLE', 429);
}

// Lecture de /cart.js, meme politique que les ecritures : 429 ou reseau, une relance.
async function fetchCart() {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    let response;
    try {
      response = await fetch(endpoint.cart(), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    } catch {
      if (attempt === 0) {
        await wait(RETRY_DELAY_MS);
        continue;
      }
      throw new ShCartError(TRY_AGAIN, 'SERVICE_UNAVAILABLE');
    }
    if (response.status === 429 && attempt === 0) {
      await wait(retryDelay(response));
      continue;
    }
    if (!response.ok) throw new ShCartError(TRY_AGAIN, 'SERVICE_UNAVAILABLE', response.status);
    return response.json();
  }
  throw new ShCartError(TRY_AGAIN, 'SERVICE_UNAVAILABLE', 429);
}

// Re-rendu serveur des sections panier (tiroir en mode hydratation, /cart en entier) quand
// la page ne reflete plus le panier et qu'aucune ecriture n'a eu lieu.
function rerenderCartSections() {
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (!(el instanceof HTMLElement) || !el.dataset.sectionId) continue;
    sectionRenderer
      .renderSection(el.dataset.sectionId, { cache: false, mode: el.hasAttribute('data-drawer') ? 'hydration' : 'full' })
      .catch(() => undefined);
  }
}

// Une app qui reecrit /cart/add.js ou /cart/change.js (AOV.ai Bundle) renvoie une reponse SANS
// `sections` : le tiroir s'ouvrait alors sur son ancien contenu (« Your cart is empty ») le temps
// d'un re-rendu en arriere-plan, soit une page vide plein ecran sur mobile (Dimi, 15/09/2026).
// On va chercher le rendu manquant AVANT de resoudre la promesse : le tiroir s'ouvre deja rempli.
async function withSections(sections, ids) {
  const have = sections && typeof sections === 'object' ? sections : {};
  const missing = ids.filter((id) => typeof have[id] !== 'string');
  if (!missing.length) return have;
  try {
    const response = await fetch(`${window.location.pathname}?sections=${missing.join(',')}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return have;
    const data = await response.json();
    return { ...have, ...(data && typeof data === 'object' ? data : {}) };
  } catch {
    return have;
  }
}

// L'app de bundle ajoute ses cadeaux APRES notre ecriture (update.js en asynchrone). Deux relectures
// courtes : si le panier a bouge depuis notre rendu, on re-rend le tiroir et le compteur du header,
// puis onChange(panier relu) rejoue les regles (la remise de l'app change le sous-total du palier).
function followAppWrites(cart, onChange) {
  if (!cart) return;
  // Cles de ligne comprises : l'app reecrit les proprietes (nouvelle cle) sans changer ni le compte
  // ni le total ; sans re-rendu, les boutons du tiroir gardaient une cle perimee (16/09/2026).
  const signature = (c) =>
    `${c.item_count}|${c.total_price}|${(c.items || []).map((item) => `${item.key}:${item.quantity}`).join(',')}`;
  let last = signature(cart);
  let done = false;
  [1200, 2800].forEach((delay) => {
    setTimeout(async () => {
      if (done) return;
      try {
        const again = await fetchCart();
        if (signature(again) !== last) {
          last = signature(again);
          done = true;
          rerenderCartSections();
          for (const el of document.querySelectorAll('sh-cart-count')) {
            if (typeof el.refresh === 'function') el.refresh();
          }
          if (typeof onChange === 'function') onChange(again);
        }
      } catch {
        /* relecture facultative */
      }
    }, delay);
  });
}

// Cle a jour d'une ligne : la meme si elle existe encore, sinon celle de l'unique ligne payante de
// la meme variante (hors cadeaux du theme ou de l'app), sinon '' (aucun nouvel essai possible).
async function currentLineKey(key) {
  const cart = await fetchCart();
  const items = Array.isArray(cart.items) ? cart.items : [];
  if (items.some((item) => item.key === key)) return key;
  const variant = String(key).split(':')[0];
  const matches = items.filter(
    (item) =>
      String(item.variant_id) === variant &&
      !(item.properties && item.properties._sh_gift) &&
      !(Number(item.final_line_price) === 0 && Number(item.original_line_price) > 0)
  );
  return matches.length === 1 ? matches[0].key : '';
}

const sourceOf = (options) => (options.source instanceof Element && options.source.isConnected ? options.source : document);
const bodyFor = (payload, sections) => ({ ...payload, sections: sections.join(','), sections_url: window.location.pathname });

class ShCart {
  #queue = Promise.resolve();
  #rules = [];
  #applyingRules = false;
  #converting = false;

  // Adaptateurs du set (D14). 'none' : rien n'est converti, le theme cache « Make it the set ».
  engines = {
    none: {
      ready: false,
      async convert() {
        throw new ShCartError('Set engine not configured (sh_set_engine = none)', 'INVALID');
      },
      async add() {
        throw new ShCartError('Set engine not configured (sh_set_engine = none)', 'INVALID');
      },
    },
    // Fiches « {Diffuser} Set, n Oils » a prix fixe : ajoute la ligne set (proprietes = parfums),
    // puis retire la ligne nue et les huiles absorbees.
    // - Avant l'ajout, relecture du panier : si une cle a retirer n'y est plus (panier change
    //   depuis le rendu de la garde), rien n'est ecrit, le tiroir est re-rendu.
    // - Si l'ajout echoue, rien n'est retire.
    // - Si le retrait echoue : une nouvelle tentative sur les lignes encore presentes, jamais un
    //   second ajout ; en dernier recours la ligne set est remise a sa quantite d'avant l'ajout
    //   (0 si elle n'existait pas ; un set identique deja au panier fusionne sur la meme cle et
    //   n'est pas retire) : le client ne paie jamais le set et la ligne nue ensemble. Puis
    //   l'erreur remonte.
    // A recetter sur les fiches TEST (C10) avant d'activer set_products.
    set_products: {
      ready: true,
      // Vente directe du set (Build your set) : une seule ligne, aucun retrait.
      async add(api, payload, options) {
        if (!payload.setVariantId) throw new ShCartError('Missing set variant', 'INVALID');
        const line = { id: payload.setVariantId, quantity: 1 };
        if (payload.properties) line.properties = payload.properties;
        return api.add([line], options);
      },
      async convert(api, payload, options) {
        if (!payload.setVariantId) throw new ShCartError('Missing set variant', 'INVALID');
        const keys = (payload.removeKeys || []).filter(Boolean);
        const tryAgain = options.tryAgain || TRY_AGAIN;

        const before = await api.cart();
        const present = new Set((before.items || []).map((item) => item.key));
        if (!keys.length || keys.some((key) => !present.has(key))) {
          api.rerender();
          throw new ShCartError(tryAgain, 'INVALID');
        }

        const line = { id: payload.setVariantId, quantity: 1 };
        if (payload.properties) line.properties = payload.properties;
        const added = await api.add([line], options);
        const setKey = ((added && added.added) || []).map((item) => item && item.key).find(Boolean) || '';

        const removeLines = (list) => api.update(Object.fromEntries(list.map((key) => [key, 0])), options);
        try {
          await removeLines(keys);
          return;
        } catch (removeError) {
          try {
            const now = await api.cart();
            const still = keys.filter((key) => (now.items || []).some((item) => item.key === key));
            if (!still.length) return;
            await removeLines(still);
            return;
          } catch {
            /* compensation ci-dessous */
          }
          if (setKey) {
            const prior = (before.items || []).find((item) => item.key === setKey);
            try {
              await api.update({ [setKey]: prior ? Number(prior.quantity) || 0 : 0 }, options);
            } catch (undoError) {
              console.warn('[sh-cart] set rollback', undoError);
            }
          }
          throw removeError;
        }
      },
    },
    // App de bundle (AOV.ai, Cart Transform) : payload exact a ecrire quand l'app sera choisie
    // et prouvee au checkout reel.
    bundle_app: {
      ready: false,
      async convert() {
        throw new ShCartError('Bundle app adapter not wired yet (D14)', 'INVALID');
      },
      // A ecrire avec payload.bundleLines / payload.components quand l'app sera choisie (D14).
      async add() {
        throw new ShCartError('Bundle app adapter not wired yet (D14)', 'INVALID');
      },
    },
  };

  // Methodes sans file d'attente, pour un adaptateur deja dans la file.
  #internal = {
    add: (lines, options = {}) => this.#add(lines, options),
    update: (updates, options = {}) => this.#update(updates, options),
    change: (key, quantity, options = {}) => this.#change(key, quantity, options),
    cart: () => fetchCart(),
    rerender: () => rerenderCartSections(),
  };

  /* API publique : lignes = [{ id, quantity, properties, selling_plan }],
     options = { source, context, tryAgain } */
  add(lines, options = {}) {
    return this.#enqueue(() => this.#add(lines, options));
  }

  change(key, quantity, options = {}) {
    return this.#enqueue(() => this.#change(key, quantity, options));
  }

  remove(key, options = {}) {
    return this.change(key, 0, options);
  }

  update(updates, options = {}) {
    return this.#enqueue(() => this.#update(updates, options));
  }

  // Une seule conversion a la fois : les clics suivants (meme tuile ou autre declencheur de la
  // garde) sont ignores jusqu'a la fin, quel que soit l'etat du DOM morphe. Renvoie null si ignore.
  convertToSet(payload, options = {}) {
    if (this.#converting) return Promise.resolve(null);
    const engine = this.engines[options.engine || 'none'] || this.engines.none;
    this.#converting = true;
    const run = this.#enqueue(() => engine.convert(this.#internal, payload || {}, options));
    const release = () => {
      this.#converting = false;
    };
    run.then(release, release);
    return run;
  }

  // Vente directe d'un set (Build your set, A1) : pas de conversion, donc pas de verrou
  // #converting ; la file d'attente suffit. payload = { setVariantId, properties } ou, pour une
  // app de bundle, les champs que son adaptateur lira.
  addSet(payload, options = {}) {
    const engine = this.engines[options.engine || 'none'] || this.engines.none;
    return this.#enqueue(() => engine.add(this.#internal, payload || {}, options));
  }

  getCart() {
    return fetchCart();
  }

  // Regle apres ecriture (cadeau de palier, huiles offertes D11) : fn(cart) renvoie
  // { add: [lignes] } et/ou { updates: { cle: quantite } } ou rien. Active : cadeau du palier 2
  // (tierGiftRule, en bas de fichier, si #sh-tier-gift est rendu).
  addRule(rule) {
    if (typeof rule === 'function') this.#rules.push(rule);
  }

  #enqueue(task) {
    const run = this.#queue.then(task, task);
    this.#queue = run.catch(() => undefined);
    return run;
  }

  #emit(action, lines, options) {
    const deferred = CartLinesUpdateEvent.createPromise();
    if (!lines.length) return deferred;
    try {
      sourceOf(options).dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: options.context || (action === 'add' ? 'product' : 'cart'),
          lines,
          promise: deferred.promise,
          detail: { source: 'sh-cart' },
        })
      );
    } catch (error) {
      console.warn('[sh-cart] CartLinesUpdateEvent', error);
    }
    return deferred;
  }

  // cart peut manquer (panier illisible apres une ecriture) : sans detail.items, le compteur
  // relit /cart.js ; sans detail.sections, cart-items-component re-rend la section cote serveur.
  #result(cart, sections, didError) {
    return {
      cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
      detail: {
        sections: sections || {},
        items: cart ? cart.items : undefined,
        itemCount: cart ? cart.item_count : undefined,
        source: 'sh-cart',
        didError,
      },
    };
  }

  // Erreur : CartErrorEvent, promesse resolue sur le panier reel (le tiroir se stabilise),
  // puis l'erreur remonte a l'appelant qui affiche le message. La promesse n'est jamais
  // rejetee : panier illisible, elle est resolue sans panier et le tiroir se re-rend.
  async #fail(deferred, error, options) {
    const failure =
      error instanceof ShCartError ? error : new ShCartError(options.tryAgain || TRY_AGAIN, 'SERVICE_UNAVAILABLE');
    try {
      sourceOf(options).dispatchEvent(new CartErrorEvent({ error: failure.message, code: failure.code }));
    } catch (eventError) {
      console.warn('[sh-cart] CartErrorEvent', eventError);
    }
    let cart = null;
    try {
      cart = await fetchCart();
    } catch (refreshError) {
      console.warn('[sh-cart] cart.js', refreshError);
    }
    deferred.resolve(this.#result(cart, undefined, true));
    throw failure;
  }

  async #add(lines, options) {
    const items = (lines || [])
      .filter((line) => line && line.id)
      .map((line) => {
        const item = { id: Number(line.id), quantity: Math.max(1, Number(line.quantity) || 1) };
        if (line.properties && Object.keys(line.properties).length) item.properties = line.properties;
        if (line.selling_plan) item.selling_plan = line.selling_plan;
        return item;
      });
    if (!items.length) throw new ShCartError('No line to add', 'INVALID');
    const sections = cartSectionIds();
    // Ajout fait par une regle (cadeau de palier) : 'update', le tiroir ne s'ouvre pas tout seul.
    const deferred = this.#emit(
      options.silent ? 'update' : 'add',
      items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      options
    );
    let added;
    try {
      added = await post(endpoint.add(), bodyFor({ items }, sections), options.tryAgain || TRY_AGAIN);
    } catch (error) {
      return this.#fail(deferred, error, options);
    }
    // AOV.ai (offre de volume active sur la fiche) AVALE l'ajout : elle envoie son propre ajout (piece
    // x palier + cadeaux) et rend {} a la place de notre requete, les autres lignes (huiles du set,
    // upsells coches) ne partent jamais (Dimi, 16/09/2026). Reponse sans articles = ajout avale : les
    // lignes apres la premiere (la piece, geree par l'app) repartent seules. La piece est deja au
    // panier : un echec ici ne remonte pas (pas de second ajout de la piece par un nouveau clic).
    if (items.length > 1 && !Array.isArray(added.items) && !added.id && !added.key) {
      try {
        const extra = await post(endpoint.add(), bodyFor({ items: items.slice(1) }, sections), options.tryAgain || TRY_AGAIN);
        added = { ...extra, items: Array.isArray(extra.items) ? extra.items : [] };
      } catch (extraError) {
        console.warn('[sh-cart] lignes apres un ajout avale par une app', extraError);
      }
    }
    // /cart/add.js a repondu 2xx : l'article est au panier. A partir d'ici plus aucune erreur
    // ne remonte (sinon « Try again » et un second clic ajouterait l'article deux fois).
    let cart = null;
    try {
      cart = await fetchCart();
    } catch (readError) {
      console.warn('[sh-cart] cart.js after add', readError);
    }
    const rendered = await withSections(added.sections, sections);
    // Editeur : panier relu vide malgre l'ajout (cookie refuse). didError garde le tiroir ferme.
    const editorLost = inEditor() && Boolean(cart) && Number(cart.item_count) === 0;
    const result = this.#result(cart, rendered, editorLost);
    deferred.resolve(result);
    if (editorLost) return { ...result, added: [], editorLost };
    followAppWrites(cart, this.#afterAppWrite);
    if (cart) await this.#runRules(cart, options, true);
    // lignes ajoutees (cles de ligne) pour un adaptateur ; hors de la promesse de l'evenement
    return { ...result, added: Array.isArray(added.items) ? added.items : [] };
  }

  async #change(key, quantity, options) {
    if (!key) throw new ShCartError('Missing line key', 'INVALID');
    const qty = Math.max(0, Number(quantity) || 0);
    const sections = cartSectionIds();
    const deferred = this.#emit(qty > 0 ? 'update' : 'remove', [{ id: String(key), quantity: qty }], options);
    const tryAgain = options.tryAgain || TRY_AGAIN;
    try {
      let cart;
      try {
        cart = await post(endpoint.change(), bodyFor({ id: String(key), quantity: qty }, sections), tryAgain);
      } catch (firstError) {
        // Un seul nouvel essai (Dimi, 16/09/2026 : « Try again » sur le « + » d'un diffuseur a 8).
        // L'app de bundle reecrit les proprietes de ligne apres nos ecritures : la cle change et le
        // bouton peut porter l'ancienne. Cle disparue : meme variante, nouvelle cle. Cle toujours la
        // (429, reseau) : meme requete apres une courte pause. Sinon l'erreur d'origine remonte.
        const retryKey = await currentLineKey(String(key)).catch(() => '');
        if (!retryKey) throw firstError;
        if (retryKey === String(key)) await wait(600);
        cart = await post(endpoint.change(), bodyFor({ id: retryKey, quantity: qty }, sections), tryAgain);
      }
      const rendered = await withSections(cart.sections, sections);
      const result = this.#result(cart.items ? cart : await fetchCart().catch(() => null), rendered, false);
      deferred.resolve(result);
      followAppWrites(cart.items ? cart : null, this.#afterAppWrite);
      await this.#runRules(cart, options);
      return result;
    } catch (error) {
      return this.#fail(deferred, error, options);
    }
  }

  async #update(updates, options) {
    const entries = Object.entries(updates || {});
    if (!entries.length) throw new ShCartError('Nothing to update', 'INVALID');
    const sections = cartSectionIds();
    const lines = entries.map(([id, quantity]) => ({ id: String(id), quantity: Math.max(0, Number(quantity) || 0) }));
    const deferred = this.#emit(lines.some((line) => line.quantity > 0) ? 'update' : 'remove', lines, options);
    try {
      const cart = await post(endpoint.update(), bodyFor({ updates }, sections), options.tryAgain || TRY_AGAIN);
      const rendered = await withSections(cart.sections, sections);
      const result = this.#result(cart.items ? cart : await fetchCart().catch(() => null), rendered, false);
      deferred.resolve(result);
      followAppWrites(cart.items ? cart : null, this.#afterAppWrite);
      await this.#runRules(cart, options);
      return result;
    } catch (error) {
      return this.#fail(deferred, error, options);
    }
  }

  // Panier modifie par une app apres notre ecriture : les regles repassent sur le panier relu.
  #afterAppWrite = (cart) => {
    this.#enqueue(() => this.#runRules(cart, {}, true));
  };

  // Au chargement, quand le rendu serveur signale un panier hors regle (cadeau de palier manquant
  // ou en trop) : une relecture et une passe des regles, sans ouvrir le tiroir.
  reconcile() {
    return this.#enqueue(async () => {
      try {
        const cart = await fetchCart();
        await this.#runRules(cart, {}, true);
      } catch (error) {
        console.warn('[sh-cart] reconcile', error);
      }
    });
  }

  // Deux passes au plus par action du client : la seconde relit le panier apres les ecritures des
  // regles pour verifier ce que Shopify a vraiment applique (cadeau offert ou non). Une ecriture
  // faite par une regle ne relance jamais les regles (garde anti boucle).
  // fresh : le panier vient de /cart.js. Sinon (reponse de change.js ou update.js, dont les
  // remises de commande n'ont pas la meme forme) on relit /cart.js avant de juger une regle.
  async #runRules(cart, options, fresh = false) {
    if (this.#applyingRules || !this.#rules.length || options.silent) return;
    this.#applyingRules = true;
    try {
      let current = fresh ? cart : await fetchCart().catch(() => null);
      for (let pass = 0; pass < 2 && current; pass += 1) {
        let wrote = false;
        for (const rule of this.#rules) {
          let operation = null;
          try {
            operation = rule(current);
          } catch (error) {
            console.warn('[sh-cart] rule', error);
          }
          if (!operation) continue;
          const ruleOptions = { ...options, silent: true };
          if (operation.add && operation.add.length) {
            await this.#add(operation.add, ruleOptions);
            wrote = true;
          }
          if (operation.updates && Object.keys(operation.updates).length) {
            await this.#update(operation.updates, ruleOptions);
            wrote = true;
          }
        }
        if (!wrote || pass === 1) break;
        current = await fetchCart().catch(() => null);
      }
    } catch (error) {
      console.warn('[sh-cart] rule write', error);
    } finally {
      this.#applyingRules = false;
    }
  }
}

/* Declencheurs delegues (une ecoute sur document, survit aux morphs du tiroir) */
function parseJSON(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// Message : element vise par data-sh-cart-status="#id", sinon [data-sh-cart-message] du
// [data-sh-cart-scope] le plus proche.
function showMessage(button, message) {
  let target = button.dataset.shCartStatus ? document.querySelector(button.dataset.shCartStatus) : null;
  if (!(target instanceof HTMLElement)) {
    const scope = button.closest('[data-sh-cart-scope]') || button.parentElement;
    target = scope ? scope.querySelector('[data-sh-cart-message]') : null;
  }
  if (!(target instanceof HTMLElement)) return;
  target.textContent = message;
  target.hidden = !message;
}

// Libelle « Try again » surchargeable par data-sh-try-again (reglage sh_cart_try_again).
function tryAgainLabel(button) {
  const holder = button.closest('[data-sh-try-again]');
  return (holder instanceof HTMLElement && holder.dataset.shTryAgain) || TRY_AGAIN;
}

// Verrou par bouton tenu en JavaScript : un morph du tiroir retire aria-busy (attribut absent
// du HTML serveur), il ne doit pas rendre le bouton cliquable pendant la requete.
const busyButtons = new WeakSet();
const nextTask = () => new Promise((resolve) => setTimeout(resolve, 0));

// Annonce polie du nouvel etat (titre « Your cart (n) », phrase de paliers, puis la rangee
// « Subtotal $n »), lue dans le DOM morphe : aucun texte invente (WCAG 4.1.3, D12).
function announce(root) {
  if (!(root instanceof Element) || !root.isConnected) return;
  const region = root.querySelector('[data-sh-cart-live]');
  if (!(region instanceof HTMLElement)) return;
  const text = ['.sh-cd__title', '.sh-reward__msg', '.sh-cd-totals__row']
    .map((selector) => {
      const el = root.querySelector(selector);
      return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
    })
    .filter(Boolean)
    .join('. ');
  region.textContent = '';
  if (text) {
    setTimeout(() => {
      region.textContent = text;
    }, 60);
  }
}

// Apres un retrait ou une conversion : si le bouton active a disparu (ou porte une autre
// ligne), le focus va au nom d'une ligne voisine, sinon au titre du panier (tabindex -1).
function restoreFocus(button, key, neighbourIds, root) {
  if (button.isConnected && (button.dataset.lineKey || '') === key) return;
  for (const id of neighbourIds) {
    const line = document.getElementById(id);
    const link = line ? line.querySelector('a.sh-cd-line__name') : null;
    if (link instanceof HTMLElement) {
      link.focus();
      return;
    }
  }
  const heading = root instanceof Element && root.isConnected ? root.querySelector('.sh-cd__title') : null;
  if (heading instanceof HTMLElement) {
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
  }
}

async function runButton(button, action, { refocus = false } = {}) {
  if (busyButtons.has(button)) return;
  busyButtons.add(button);
  button.setAttribute('aria-busy', 'true');
  button.setAttribute('aria-disabled', 'true');
  showMessage(button, '');

  const root = button.closest('.sh-cd');
  const key = button.dataset.lineKey || '';
  const line = button.closest('.sh-cd-line');
  const neighbourIds = line
    ? [line.nextElementSibling, line.previousElementSibling].filter((el) => el && el.id).map((el) => el.id)
    : [];

  let done = false;
  try {
    done = (await action()) !== null;
  } catch (error) {
    console.warn('[sh-cart]', error);
    showMessage(button, error instanceof ShCartError ? error.message : tryAgainLabel(button));
  } finally {
    busyButtons.delete(button);
    if (button.isConnected) {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }
  }
  if (!done) return;
  await nextTask(); // le morph du tiroir (cart-items-component) est pose
  if (refocus) restoreFocus(button, key, neighbourIds, root);
  announce(root);
}

function onCartClick(event) {
  const origin = event.target instanceof Element ? event.target : null;
  const button = origin && origin.closest('[data-sh-cart-add], [data-sh-cart-change], [data-sh-cart-convert]');
  if (!(button instanceof HTMLElement)) return;
  event.preventDefault();
  event.stopPropagation();

  const data = button.dataset;
  const options = { source: button, tryAgain: tryAgainLabel(button) };

  if (button.hasAttribute('data-sh-cart-add')) {
    const lines = parseJSON(data.lines);
    const payload = Array.isArray(lines)
      ? lines
      : [{ id: data.variantId || '', quantity: Number(data.quantity) || 1, properties: parseJSON(data.properties) || undefined }];
    runButton(button, async () => {
      try {
        const result = await hodCart.add(payload, options);
        if (result && result.editorLost) showMessage(button, EDITOR_NOTE);
        return result;
      } catch (error) {
        // Editeur : les ajouts y prennent aussi des 429 ; meme explication que le panier perdu.
        if (inEditor() && error instanceof ShCartError && error.code === 'SERVICE_UNAVAILABLE') {
          throw new ShCartError(EDITOR_NOTE, error.code, error.status);
        }
        throw error;
      }
    });
  } else if (button.hasAttribute('data-sh-cart-change')) {
    const quantity = Number.isFinite(Number(data.quantity)) ? Number(data.quantity) : 0;
    runButton(button, () => hodCart.change(data.lineKey || '', quantity, { ...options, context: 'cart' }), {
      refocus: quantity === 0,
    });
  } else {
    const payload = {
      setVariantId: data.setVariantId || '',
      properties: parseJSON(data.properties) || undefined,
      removeKeys: (data.removeKeys || '').split(',').map((key) => key.trim()).filter(Boolean),
    };
    runButton(
      button,
      () => hodCart.convertToSet(payload, { ...options, context: 'cart', engine: data.shEngine || 'none' }),
      { refocus: true }
    );
  }
}

/* Cadeau du palier 2 (Dimi, 15/09/2026 : Lavande 10 ml offerte des 110 $). Reglage rendu par
   snippets/sh-tier-gift-config.liquid dans #sh-tier-gift. Base = sous-total des lignes (remises de
   ligne comprises) moins les lignes cadeau : la valeur du cadeau ne tient jamais son propre seuil.
   Au seuil : exactement une ligne de la variante cadeau, quantite 1, propriete _sh_gift. En dessous,
   palier eteint ou variante changee : les lignes cadeau partent. Le cadeau est paye par la remise
   automatique de commande nommee config.title ; la regle ne fixe aucun prix. Verification : un
   cadeau present que Shopify ne couvre pas (remise absente, cumul refuse par une app) est retire
   et le panier est bloque tant que son contenu hors cadeau ne change pas (session). Idempotente. */
const GIFT_BLOCK_KEY = 'sh-gift-block';

function readTierGift() {
  const el = document.getElementById('sh-tier-gift');
  const data = el ? parseJSON(el.textContent) : null;
  if (!data || typeof data.key !== 'string' || !data.key) return null;
  return {
    on: data.on === true,
    threshold: Number(data.threshold) || 0,
    variant: Number(data.variant) || 0,
    key: data.key,
    value: String(data.value || 'gift'),
    title: typeof data.title === 'string' ? data.title.trim() : '',
    sync: data.sync === true,
  };
}

function readGiftBlock() {
  try {
    return window.sessionStorage.getItem(GIFT_BLOCK_KEY) || '';
  } catch {
    return '';
  }
}

function writeGiftBlock(signature) {
  try {
    if (signature) window.sessionStorage.setItem(GIFT_BLOCK_KEY, signature);
    else window.sessionStorage.removeItem(GIFT_BLOCK_KEY);
  } catch {
    /* stockage indisponible : le blocage reste en memoire */
  }
}

function tierGiftRule(config) {
  let blocked = readGiftBlock();
  return (cart) => {
    if (!cart || !Array.isArray(cart.items)) return null;
    const isGift = (item) => Boolean(item && item.properties && item.properties[config.key]);
    const gifts = cart.items.filter(isGift);
    const giftValue = gifts.reduce((sum, item) => sum + (Number(item.final_line_price) || 0), 0);
    const base = (Number(cart.items_subtotal_price) || 0) - giftValue;
    // Contenu hors cadeau : un changement leve le blocage (nouvelle tentative).
    const signature = cart.items
      .filter((item) => !isGift(item))
      .map((item) => `${item.variant_id}:${item.quantity}:${item.final_line_price}`)
      .sort()
      .join('|');
    if (blocked && blocked !== signature) {
      blocked = '';
      writeGiftBlock('');
    }
    const covered =
      gifts.length > 0 &&
      (gifts.every((item) => Number(item.final_line_price) === 0) ||
        (Array.isArray(cart.cart_level_discount_applications) &&
          cart.cart_level_discount_applications.some(
            (app) => app && String(app.title || '').trim() === config.title && Number(app.total_allocated_amount) > 0
          )));
    let wanted = config.on && config.variant > 0 && config.threshold > 0 && base >= config.threshold;
    if (wanted && gifts.length > 0 && !covered) {
      blocked = signature;
      writeGiftBlock(signature);
    }
    if (blocked === signature) wanted = false;
    const updates = {};
    let kept = false;
    for (const item of gifts) {
      const keep = wanted && !kept && Number(item.variant_id) === config.variant;
      if (keep) kept = true;
      const quantity = keep ? 1 : 0;
      if (Number(item.quantity) !== quantity) updates[item.key] = quantity;
    }
    const operation = {};
    if (wanted && !kept) {
      operation.add = [{ id: config.variant, quantity: 1, properties: { [config.key]: config.value } }];
    }
    if (Object.keys(updates).length) operation.updates = updates;
    return operation.add || operation.updates ? operation : null;
  };
}

export const hodCart = new ShCart();

if (!window.__hodCartBound) {
  window.__hodCartBound = true;
  window.ShCart = hodCart;
  document.addEventListener('click', onCartClick);
  // Regle chargee si le palier est actif, ou pour retirer un cadeau reste au panier (sync).
  const tierGift = readTierGift();
  if (tierGift && (tierGift.on || tierGift.sync)) {
    hodCart.addRule(tierGiftRule(tierGift));
    if (tierGift.sync) hodCart.reconcile();
  }
  document.dispatchEvent(new CustomEvent('sh:cart:ready', { detail: { cart: hodCart } }));
}

